/**
 * Admin V3 — Avoir (credit note) detail page
 *
 * Mounted at: /admin-v3/billing/avoirs/:avoirId
 * Source: GET /admin/billing/avoirs/:id → { avoir }  (ADMIN_PARTNER_READ).
 *
 * Lifecycle through the UI:
 *   - Download PDF        GET  …/avoirs/:id/pdf    (ADMIN_PARTNER_READ)
 *   - Issue (draft only)  POST …/avoirs/:id/issue  (ADMIN_PARTNER_UPDATE) →
 *                         assigns the legal AV-YYYY-NNNNNN number.
 *
 * RBAC: backend is authoritative; the Issue button is hidden unless
 * usePermissions().can('admin:partner:update').
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileMinus, ArrowLeft, Download, CheckCircle2, Loader2, Send } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SectionCard, SmallPill, currency, avoirStatusMeta, cn } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import usePdfDownload from '../../shared/hooks/usePdfDownload';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const PERM_UPDATE = 'admin:partner:update';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' })
      .format(typeof iso === 'string' ? new Date(iso) : iso);
  } catch { return String(iso); }
};

const Field = ({ label, children, accent }) => (
  <div className="text-[12px]">
    <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">{label}</dt>
    <dd className={cn('font-medium', accent || 'text-slate-700')}>{children}</dd>
  </div>
);

const AvoirDetailPage = () => {
  const locale = DEFAULT_LOCALE;
  const { avoirId } = useParams();
  const navigate = useNavigate();
  const { can, loading: permsLoading } = usePermissions();
  const { showSuccess, showError } = useToast();

  const { data, loading, error, refetch } = useAdminQuery(`/admin/billing/avoirs/${avoirId}`);
  const avoir = data?.avoir || null;

  const issueM = useAdminMutation(`/admin/billing/avoirs/${avoirId}/issue`, 'post');
  const resendM = useAdminMutation(`/admin/billing/avoirs/${avoirId}/resend`, 'post');
  const pdf = usePdfDownload();

  const canUpdate = can(PERM_UPDATE);
  const isDraft = avoir?.status === 'draft';
  const lineItems = Array.isArray(avoir?.credited_line_items) ? avoir.credited_line_items : [];
  const taxLabel = t('billing.invoiceDetail.field.tax', locale).replace('{rate}', String(avoir?.tax_rate ?? 0));

  const handlePdf = () => {
    pdf.download(`/admin/billing/avoirs/${avoirId}/pdf`, `${avoir?.avoir_number || 'avoir'}.pdf`)
      .then((ok) => { if (!ok) showError(pdf.error || t('billing.invoiceDetail.pdf.error', locale)); });
  };

  const handleIssue = () => {
    issueM.mutate({}, {
      // F1: the avoir is always issued (commit-then-deliver). Inspect delivery
      // so a failed email surfaces a warning instead of a green "OK".
      onSuccess: (res) => {
        if (res?.delivery && res.delivery.delivered === false) {
          const why = res.delivery.error ? `: ${res.delivery.error}` : '';
          showError(t('billing.avoirDetail.issued.warnEmail', locale) + why);
        } else {
          showSuccess(t('billing.avoirDetail.issued.ok', locale));
        }
        refetch();
      },
      onError: (e) => showError(e?.response?.data?.detail || t('billing.invoiceDetail.action.error', locale)),
    });
  };

  const handleResend = () => {
    resendM.mutate({}, {
      onSuccess: (res) => {
        if (res?.delivery && res.delivery.delivered === false) {
          const why = res.delivery.error ? `: ${res.delivery.error}` : '';
          showError(t('billing.delivery.retry.failed', locale) + why);
        } else {
          showSuccess(t('billing.delivery.retry.ok', locale));
        }
        refetch();
      },
      onError: (e) => showError(e?.response?.data?.detail || t('billing.delivery.retry.failed', locale)),
    });
  };

  return (
    <SectionCard
      title={avoir?.avoir_number || t('billing.avoirDetail.back', locale)}
      icon={FileMinus}
      iconClass="bg-amber-100 text-amber-600"
      action={
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/billing/avoirs')} data-testid="adminv3-avoir-detail-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" />{t('billing.avoirDetail.back', locale)}
        </Button>
      }
    >
      {loading && !data && (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />{t('billing.invoiceDetail.loading', locale)}
        </div>
      )}

      {error && !loading && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="adminv3-avoir-detail-error">{error}</div>
      )}

      {avoir && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SmallPill meta={avoirStatusMeta[avoir.status] || avoirStatusMeta.draft} />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={handlePdf} disabled={pdf.loading} data-testid="adminv3-avoir-pdf">
                {pdf.loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
                {t('billing.avoirDetail.action.pdf', locale)}
              </Button>
              {/* F2: while the permissions fetch is in flight, show a stable spinner
                  placeholder so the Issue button appears once (resolved) instead of
                  flickering in after the read-only PDF button. */}
              {permsLoading && (
                <span className="flex items-center px-2 text-slate-300" data-testid="adminv3-avoir-perms-loading">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                </span>
              )}
              {canUpdate && isDraft && (
                <Button variant="default" size="sm" onClick={handleIssue} disabled={issueM.loading} data-testid="adminv3-avoir-issue">
                  {issueM.loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                  {t('billing.avoirDetail.action.issue', locale)}
                </Button>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">
              {t('billing.avoirDetail.section.original', locale)}
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
              <Field label={t('billing.avoirDetail.field.originalInvoice', locale)}><span className="font-mono">{avoir.original_invoice_number || '—'}</span></Field>
              <Field label={t('billing.avoirDetail.field.type', locale)}>{avoir.avoir_type === 'partial' ? t('billing.avoirCreate.type.partial', locale) : t('billing.avoirCreate.type.full', locale)}</Field>
              <Field label={t('billing.avoirDetail.field.issued', locale)}>{fmtDate(avoir.issued_at)}</Field>
              <Field label={t('billing.avoirDetail.field.reason', locale)}>{avoir.reason || '—'}</Field>
            </dl>
          </div>

          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-3">
              {t('billing.avoirDetail.section.amounts', locale)}
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
              <Field label={t('billing.invoiceDetail.field.subtotal', locale)} accent="text-rose-700">{currency(avoir.amount ?? 0)}</Field>
              <Field label={taxLabel} accent="text-rose-700">{currency(avoir.tax_amount ?? 0)}</Field>
              <Field label={t('billing.invoiceDetail.field.total', locale)} accent="text-rose-700 font-bold">{currency(avoir.total_amount ?? 0)}</Field>
            </dl>
          </div>

          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">
              {t('billing.avoirDetail.field.partner', locale)}
            </div>
            <div className="text-slate-900 font-medium">{avoir.partner_name || '—'}</div>
            <div className="text-[12px] text-slate-500">{avoir.partner_email || ''}</div>
            {avoir.partner_vat_number && <div className="text-[12px] text-slate-500 font-mono mt-1">{avoir.partner_vat_number}</div>}
          </div>

          {/* Email delivery (F1) — visible once issued; operator sees status + can retry */}
          {avoir.status === 'issued' && (
            <div className="rounded-xl border border-slate-200 p-4" data-testid="adminv3-avoir-delivery">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                  {t('billing.delivery.section', locale)}
                </div>
                {canUpdate && (
                  <Button variant="outline" size="sm" onClick={handleResend} disabled={resendM.loading}
                          data-testid="adminv3-avoir-resend">
                    {resendM.loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                    {t('billing.delivery.retry', locale)}
                  </Button>
                )}
              </div>
              {avoir.delivered_at ? (
                <div className="text-[12px] font-medium text-emerald-700" data-testid="adminv3-avoir-delivery-status">
                  {t('billing.delivery.sent', locale)} — {fmtDate(avoir.delivered_at)}
                </div>
              ) : avoir.last_delivery_error ? (
                <div className="text-[12px] font-medium text-rose-700" data-testid="adminv3-avoir-delivery-status">
                  {t('billing.delivery.failed', locale)}
                </div>
              ) : (
                <div className="text-[12px] text-slate-500" data-testid="adminv3-avoir-delivery-status">
                  {t('billing.delivery.notSent', locale)}
                </div>
              )}
              {avoir.last_delivery_attempt_at && (
                <div className="text-[11px] text-slate-400 mt-1">
                  {t('billing.delivery.attemptAt', locale)}: {fmtDate(avoir.last_delivery_attempt_at)}
                </div>
              )}
              {avoir.last_delivery_error && (
                <div className="text-[11px] text-rose-600 mt-1 break-all" data-testid="adminv3-avoir-delivery-error">
                  {t('billing.delivery.error', locale)}: {avoir.last_delivery_error}
                </div>
              )}
            </div>
          )}

          {lineItems.length > 0 && (
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">
                {t('billing.avoirDetail.section.lineItems', locale)} ({lineItems.length})
              </div>
              <pre className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-[11px] font-mono text-slate-600 overflow-x-auto max-h-56">
{JSON.stringify(lineItems, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
};

export default AvoirDetailPage;
